/**
 *  @class
 *  @function SearchForm
 */
if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.is_drawer = this.dataset.drawer;
      this.drawer = this.closest('#Search-Drawer');
      this.form = this.querySelector('form');
      this.button = document.querySelectorAll('.thb-quick-search');
      this.input = this.querySelector('.search-field');
      this.defaultTab = this.querySelector('.side-panel-content--initial');
      this.predictiveSearchResults = this.is_drawer ? this.querySelector('.side-panel-content--has-tabs') : this.querySelector('.thb-predictive-search');
      this.cc = this.querySelector('.searchform--click-capture');
      this.promotionTemplate = this.querySelector('.thb-predictive-search--promotion-template');
      this.promotionHydrated = false;
      this.cache = new Map();
      this.cacheLimit = 50;
      this.lastSearchTerm = '';
      this.requestController = null;
      this.requestId = 0;

      this.setupEventListeners();
    }

    hydratePromotion() {
      if (!this.promotionTemplate) {
        return;
      }
      this.promotionTemplate.parentNode.insertBefore(
        this.promotionTemplate.content,
        this.promotionTemplate
      );
      this.promotionTemplate.remove();
      this.promotionTemplate = null;
      this.promotionHydrated = true;
    }

    setupEventListeners() {
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));

      this.input.addEventListener('input', debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));
      this.input.addEventListener('focus', debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));

      this.button.forEach((item) => {
        item.addEventListener('click', (event) => {
          event.preventDefault();
          this.hydratePromotion();
          if (this.is_drawer) {
            OverlayManager.open(this.drawer);
            setTimeout(() => {
              this.input.focus({
                preventScroll: true
              });
            }, 100);
          } else {
            document.body.classList.add('open-cc');
          }
          return false;
        });
      });

      if (!this.is_drawer) {
        this.onCcClickBound = this.close.bind(this);
        this.onKeyUpBound = (e) => {
          if (e.code && e.code.toUpperCase() === 'ESCAPE') {
            this.close();
          }
        };
        this.cc?.addEventListener('click', this.onCcClickBound);
        document.addEventListener('keyup', this.onKeyUpBound);
      }
    }

    disconnectedCallback() {
      if (this.requestController) {
        this.requestController.abort();
      }
      if (this.onKeyUpBound) {
        document.removeEventListener('keyup', this.onKeyUpBound);
      }
      if (this.onCcClickBound && this.cc) {
        this.cc.removeEventListener('click', this.onCcClickBound);
      }
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        if (this.requestController) {
          this.requestController.abort();
        }
        this.requestId += 1;
        this.lastSearchTerm = '';
        this.showDefaultContent();
        return;
      }
      if (!this.is_drawer) {
        this.open();
      }
      if (searchTerm === this.lastSearchTerm) {
        return;
      }
      this.lastSearchTerm = searchTerm;
      this.showSearchResults();
      this.getSearchResults(searchTerm);
    }

    showDefaultContent() {
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.classList.remove('active');
        this.predictiveSearchResults.innerHTML = '';
      }
      if (this.defaultTab) {
        this.defaultTab.style.display = '';
      }
      if (!this.is_drawer) {
        this.close();
      }
    }

    showSearchResults() {
      if (this.defaultTab) {
        this.defaultTab.style.display = 'none';
      }
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.classList.add('active');
      }
    }

    onFormSubmit(event) {
      if (!this.getQuery().length) {
        event.preventDefault();
      }
    }

    getSearchUrl(searchTerm) {
      return `${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,collection,article&${encodeURIComponent('resources[limit]')}=10&${encodeURIComponent('resources[limit_scope]')}=each&${encodeURIComponent('resources[options][fields]')}=title,body,author,tag,product_type,vendor,variants.title,variants.sku&section_id=predictive-search`;
    }

    fetchSearchMarkup(searchTerm, signal) {
      return fetch(this.getSearchUrl(searchTerm), { signal })
        .then((response) => {
          if (!response.ok) {
            throw new Error(response.status);
          }
          return response.text();
        })
        .then((text) => {
          const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
          if (!section) {
            throw new Error('Predictive search section missing');
          }
          return section.innerHTML;
        });
    }

    getFallbackTerms(searchTerm) {
      const stopWords = new Set([
        'and', 'aux', 'avec', 'des', 'die', 'ein', 'eine', 'for', 'les', 'mit',
        'pour', 'the', 'und', 'une', 'von', 'with'
      ]);
      const terms = searchTerm.match(/[\p{L}\p{N}]+/gu) || [];
      const seen = new Set();

      return terms.filter((term) => {
        const normalizedTerm = term
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase();

        if (normalizedTerm.length < 3 || stopWords.has(normalizedTerm) || seen.has(normalizedTerm)) {
          return false;
        }
        seen.add(normalizedTerm);
        return true;
      }).slice(0, 6);
    }

    hasProductResults(resultsMarkup) {
      const parsed = new DOMParser().parseFromString(`<div id="predictive-search-root">${resultsMarkup}</div>`, 'text/html');
      return Boolean(parsed.querySelector('[data-predictive-resource="product"][data-predictive-role="panel"] product-card-small'));
    }

    mergeFallbackProducts(primaryMarkup, fallbackMarkups) {
      const parsed = new DOMParser().parseFromString(`<div id="predictive-search-root">${primaryMarkup}</div>`, 'text/html');
      const root = parsed.querySelector('#predictive-search-root');
      const tabs = root.querySelector('side-panel-content-tabs');
      const panels = root.querySelector('.side-panel-content--inner');
      const fallbackCards = [];
      const seenProducts = new Set();
      let productTab = null;
      let productPanel = null;

      fallbackMarkups.forEach((markup) => {
        if (!markup) {
          return;
        }
        const fallback = new DOMParser().parseFromString(`<div id="predictive-search-fallback">${markup}</div>`, 'text/html');
        const fallbackRoot = fallback.querySelector('#predictive-search-fallback');
        const fallbackTab = fallbackRoot.querySelector('[data-predictive-resource="product"][data-predictive-role="tab"]');
        const fallbackPanel = fallbackRoot.querySelector('[data-predictive-resource="product"][data-predictive-role="panel"]');

        if (!productTab && fallbackTab) {
          productTab = fallbackTab.cloneNode(true);
        }
        if (!productPanel && fallbackPanel) {
          productPanel = fallbackPanel.cloneNode(false);
        }
        fallbackPanel?.querySelectorAll('product-card-small').forEach((card) => {
          const productKey = card.dataset.url || card.dataset.productHandle || card.textContent.trim();
          if (!seenProducts.has(productKey) && fallbackCards.length < 10) {
            seenProducts.add(productKey);
            fallbackCards.push(card.cloneNode(true));
          }
        });
      });

      if (!tabs || !panels || !productTab || !productPanel || !fallbackCards.length) {
        return primaryMarkup;
      }

      tabs.querySelectorAll('button').forEach((button) => button.classList.remove('tab-active'));
      panels.querySelectorAll('.side-panel-content--tab-panel').forEach((panel) => panel.classList.remove('tab-active'));
      productTab.classList.add('tab-active');
      productPanel.classList.add('tab-active');
      fallbackCards.forEach((card) => productPanel.appendChild(card));
      tabs.insertBefore(productTab, tabs.firstChild);
      panels.insertBefore(productPanel, panels.firstChild);

      return root.innerHTML;
    }

    async getSearchResults(searchTerm) {
      const cached = this.cache.get(searchTerm);
      if (cached) {
        if (searchTerm === this.getQuery()) {
          this.predictiveSearchResults?.classList.remove('loading');
          this.renderSearchResults(cached);
        }
        return;
      }

      if (!this.predictiveSearchResults) {
        return;
      }

      if (this.requestController) {
        this.requestController.abort();
      }
      this.requestController = new AbortController();
      const signal = this.requestController.signal;
      const requestId = ++this.requestId;

      this.predictiveSearchResults.classList.add('loading');

      try {
        let resultsMarkup = await this.fetchSearchMarkup(searchTerm, signal);
        const fallbackTerms = this.getFallbackTerms(searchTerm);
        const shouldUseFallback = fallbackTerms.some((term) => term.toLocaleLowerCase() !== searchTerm.toLocaleLowerCase());

        if (!this.hasProductResults(resultsMarkup) && shouldUseFallback) {
          const fallbackMarkups = await Promise.all(
            fallbackTerms.map((term) => this.fetchSearchMarkup(term, signal).catch((error) => {
              if (error.name === 'AbortError') {
                throw error;
              }
              return null;
            }))
          );
          resultsMarkup = this.mergeFallbackProducts(resultsMarkup, fallbackMarkups);
        }

        if (requestId !== this.requestId || searchTerm !== this.getQuery()) {
          return;
        }

        if (this.cache.size >= this.cacheLimit) {
          this.cache.delete(this.cache.keys().next().value);
        }
        this.cache.set(searchTerm, resultsMarkup);
        this.renderSearchResults(resultsMarkup);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(error);
        }
      } finally {
        if (requestId === this.requestId) {
          this.predictiveSearchResults.classList.remove('loading');
        }
      }
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      const submitBtn = this.predictiveSearchResults.querySelector('#search-results-submit');
      if (submitBtn) {
        if (!this.is_drawer) {
          submitBtn.classList.remove('button');
        }
        submitBtn.addEventListener('click', () => {
          this.form.submit();
        });
      }
    }

    close() {
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.setAttribute('inert', '');
      }
      document.body.classList.remove('open-search');
    }
    open() {
      document.body.classList.add('open-search');
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.removeAttribute('inert');
      }
    }
  }
  customElements.define('search-form', SearchForm);
}
